import { useState, useCallback } from 'react';
import { RefreshCw, Settings } from 'lucide-react';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { useVendorCostAnalysis } from '../hooks/useVendorCostAnalysis';
import { SearchableSelect } from '../components/ui/SearchableSelect';
import { MultiSearchableSelect } from '../components/ui/MultiSearchableSelect';
import { OverviewTab } from '../components/vendor-cost-analysis/OverviewTab';
import { ByVendorTab } from '../components/vendor-cost-analysis/ByVendorTab';
import { VendorGroupsTab } from '../components/vendor-cost-analysis/VendorGroupsTab';
import { CategorySettingsTab } from '../components/vendor-cost-analysis/CategorySettingsTab';
import type { VCATab, VCAViewMode } from '../types/vendorCostAnalysis';

const TABS: { key: VCATab; label: string; icon?: typeof Settings }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'by-vendor', label: 'By Vendor' },
    { key: 'vendor-groups', label: 'Group Settings', icon: Settings },
    { key: 'category-settings', label: 'Category Settings', icon: Settings },
];

function fmtDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function getPeriodDates(preset: string, year: number): { from: string; to: string } {
    const now = new Date();
    const lastCompleteMonth = year < now.getFullYear() ? 12 : now.getMonth();

    switch (preset) {
        case 'YTD': {
            const end = new Date(year, lastCompleteMonth, 0);
            return { from: `${year}-01-01`, to: fmtDate(end) };
        }
        case 'MTD': {
            const start = new Date(year, lastCompleteMonth - 1, 1);
            const end = new Date(year, lastCompleteMonth, 0);
            return { from: fmtDate(start), to: fmtDate(end) };
        }
        case '3M': {
            const start = new Date(year, lastCompleteMonth - 3, 1);
            const end = new Date(year, lastCompleteMonth, 0);
            return { from: fmtDate(start), to: fmtDate(end) };
        }
        default: {
            const end = new Date(year, lastCompleteMonth, 0);
            return { from: `${year}-01-01`, to: fmtDate(end) };
        }
    }
}

function detectPeriod(dateFrom: string, dateTo: string, year: number): string {
    for (const preset of ['YTD', 'MTD', '3M']) {
        const dates = getPeriodDates(preset, year);
        if (dates.from === dateFrom && dates.to === dateTo) return preset;
    }
    return '';
}

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => currentYear - i);

function loadSessionValue<T>(key: string, fallback: T): T {
    try { const v = sessionStorage.getItem(key); return v ? JSON.parse(v) : fallback; } catch { return fallback; }
}

export function VendorCostAnalysisPage() {
    const { canEdit, isLoading: permLoading } = usePagePermissions('/vendor-cost-analysis');
    const { filterOptions, filters, updateFilters, loading, error } = useVendorCostAnalysis();
    const [activeTab, setActiveTab] = useState<VCATab>('overview');
    const [groupSearchTerm, setGroupSearchTerm] = useState('');
    const [refreshKey, setRefreshKey] = useState(0);
    const [selectedYear, setSelectedYear] = useState(() => loadSessionValue('vca-year', currentYear));
    const [activePeriod, setActivePeriod] = useState<string>(() => detectPeriod(filters.dateFrom, filters.dateTo, loadSessionValue('vca-year', currentYear)));
    const [viewMode, setViewMode] = useState<VCAViewMode>(() => loadSessionValue('vca-viewMode', 'vendor' as VCAViewMode));

    const handleYearChange = useCallback((year: number) => {
        setSelectedYear(year);
        try { sessionStorage.setItem('vca-year', JSON.stringify(year)); } catch { /* ignore */ }
        if (activePeriod) {
            const dates = getPeriodDates(activePeriod, year);
            updateFilters({ dateFrom: dates.from, dateTo: dates.to });
        }
    }, [activePeriod, updateFilters]);

    const handlePeriodClick = useCallback((preset: string) => {
        setActivePeriod(preset);
        const dates = getPeriodDates(preset, selectedYear);
        updateFilters({ dateFrom: dates.from, dateTo: dates.to });
    }, [selectedYear, updateFilters]);

    const handleDateChange = useCallback((field: 'dateFrom' | 'dateTo', value: string) => {
        const newFrom = field === 'dateFrom' ? value : filters.dateFrom;
        const newTo = field === 'dateTo' ? value : filters.dateTo;
        setActivePeriod(detectPeriod(newFrom, newTo, selectedYear));
        updateFilters({ [field]: value });
    }, [updateFilters, filters.dateFrom, filters.dateTo, selectedYear]);

    const handleViewModeToggle = useCallback((mode: VCAViewMode) => {
        setViewMode(mode);
        try { sessionStorage.setItem('vca-viewMode', JSON.stringify(mode)); } catch { /* ignore */ }
        // Decision: vendorIDs and groupVendorIDs are kept independently — switching modes
        // preserves both selections so the user can flip back and forth without re-picking.
    }, []);

    // Pre-filter vendors/categories to those with data in selected company
    const filteredVendors = filterOptions.vendors
        .filter(v => !filters.company || v.companyCode === filters.company)
        .map(v => ({ id: v.id, name: `${v.name} (${v.code})` }));

    const filteredCategories = filterOptions.categories
        .map(c => ({ id: c.code, name: c.name }));

    const filteredGroups = filterOptions.vendorGroups
        .map(g => ({ id: String(g.id), name: `${g.name} (${g.memberCount})` }));

    if (permLoading || loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <h1 className="text-2xl font-bold text-gray-900">Vendor Cost Analysis</h1>
                    {canEdit && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                            Edit Access
                        </span>
                    )}
                </div>
                <button
                    onClick={() => setRefreshKey(k => k + 1)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 border rounded-md hover:bg-gray-50"
                >
                    <RefreshCw className="w-4 h-4" />
                    Refresh
                </button>
            </div>

            {/* Filter Bar */}
            <div className="bg-white rounded-lg shadow-sm border p-4">
                <div className="flex flex-wrap items-end gap-4">
                    {/* Company */}
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-gray-500">Company</label>
                        <select
                            value={filters.company}
                            onChange={e => updateFilters({ company: e.target.value })}
                            className="border rounded-md px-3 py-1.5 text-sm bg-white"
                        >
                            <option value="">All Companies</option>
                            {filterOptions.companies.map(c => (
                                <option key={c.code} value={c.code}>{c.name} ({c.code})</option>
                            ))}
                        </select>
                    </div>

                    {/* Year */}
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-gray-500">Year</label>
                        <select
                            value={selectedYear}
                            onChange={e => handleYearChange(parseInt(e.target.value, 10))}
                            className="border rounded-md px-3 py-1.5 text-sm bg-white"
                        >
                            {YEARS.map(y => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                    </div>

                    {/* Period Presets */}
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-gray-500">Period</label>
                        <div className="flex border rounded-md overflow-hidden">
                            {['YTD', 'MTD', '3M'].map(p => (
                                <button
                                    key={p}
                                    onClick={() => handlePeriodClick(p)}
                                    className={`px-3 py-1.5 text-sm ${
                                        activePeriod === p
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-white text-gray-600 hover:bg-gray-50'
                                    }`}
                                >
                                    {p === '3M' ? '3 Months' : p}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Date From */}
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-gray-500">From</label>
                        <input
                            type="date"
                            value={filters.dateFrom}
                            onChange={e => handleDateChange('dateFrom', e.target.value)}
                            className="border rounded-md px-3 py-1.5 text-sm"
                        />
                    </div>

                    {/* Date To */}
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-gray-500">To</label>
                        <input
                            type="date"
                            value={filters.dateTo}
                            onChange={e => handleDateChange('dateTo', e.target.value)}
                            className="border rounded-md px-3 py-1.5 text-sm"
                        />
                    </div>

                    {/* Vendor / Vendor Group Toggle */}
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-gray-500">View</label>
                        <div className="flex border rounded-md overflow-hidden">
                            <button
                                onClick={() => handleViewModeToggle('vendor')}
                                className={`px-3 py-1.5 text-sm ${
                                    viewMode === 'vendor'
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-white text-gray-600 hover:bg-gray-50'
                                }`}
                            >
                                Vendor
                            </button>
                            <button
                                onClick={() => handleViewModeToggle('vendorGroup')}
                                className={`px-3 py-1.5 text-sm ${
                                    viewMode === 'vendorGroup'
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-white text-gray-600 hover:bg-gray-50'
                                }`}
                            >
                                Vendor Group
                            </button>
                        </div>
                    </div>

                    {/* Category (searchable) */}
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-gray-500">Category</label>
                        <SearchableSelect
                            options={filteredCategories}
                            value={filters.categoryCode}
                            onChange={v => updateFilters({ categoryCode: v })}
                            placeholder="All Categories"
                            className="w-44"
                        />
                    </div>

                    {/* Vendor or Group (multi-searchable) */}
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-gray-500">
                            {viewMode === 'vendor' ? 'Vendor' : 'Vendor Group'}
                        </label>
                        <MultiSearchableSelect
                            options={viewMode === 'vendor' ? filteredVendors : filteredGroups}
                            values={viewMode === 'vendor' ? filters.vendorIDs : filters.groupVendorIDs}
                            onChange={v => updateFilters(viewMode === 'vendor' ? { vendorIDs: v } : { groupVendorIDs: v })}
                            placeholder={viewMode === 'vendor' ? 'All Vendors' : 'All Groups'}
                            className="w-72"
                        />
                    </div>
                </div>
            </div>

            {/* Error */}
            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {error}
                </div>
            )}

            {/* Tabs */}
            <div className="bg-white rounded-lg shadow-sm border">
                <div className="border-b">
                    <nav className="flex -mb-px">
                        {TABS.map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
                                    activeTab === tab.key
                                        ? 'border-blue-600 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                {tab.icon && <tab.icon className="w-3.5 h-3.5" />}
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>

                <div className="p-4">
                    {activeTab === 'overview' && (
                        <OverviewTab
                            filters={filters}
                            selectedYear={selectedYear}
                            refreshKey={refreshKey}
                            viewMode={viewMode}
                            onFilterChange={updateFilters}
                        />
                    )}
                    {activeTab === 'by-vendor' && (
                        <ByVendorTab
                            filters={filters}
                            refreshKey={refreshKey}
                            viewMode={viewMode}
                            onViewModeChange={handleViewModeToggle}
                            onNavigateToGroup={(groupName) => {
                                setGroupSearchTerm(groupName);
                                setActiveTab('vendor-groups');
                            }}
                        />
                    )}
                    {activeTab === 'vendor-groups' && (
                        <VendorGroupsTab
                            filters={filters}
                            refreshKey={refreshKey}
                            canEdit={canEdit}
                            initialSearch={groupSearchTerm}
                            onInitialSearchConsumed={() => setGroupSearchTerm('')}
                        />
                    )}
                    {activeTab === 'category-settings' && (
                        <CategorySettingsTab canEdit={canEdit} />
                    )}
                </div>
            </div>
        </div>
    );
}
